import type { HelpContent } from './types';

export const projectForecastHelp: HelpContent = {
  pageTitle: 'Project Forecast Report',
  description: 'View forecast performance across your portfolio — comparing budgeted costs against estimated costs at completion to identify projects where the final outturn may differ from plan.',

  functionality: {
    overview: 'The Project Forecast Report shows how each project is tracking against its budget by comparing the original budget to the Estimate at Completion (EAC). It helps you identify early where projects are likely to overspend or underspend before it becomes a problem.',

    keyFeatures: [
      {
        label: 'Estimate at Completion (EAC)',
        detail: 'The EAC is calculated for each project based on actual spend to date and the remaining forecast, giving you a projected final cost.',
      },
      {
        label: 'Budget vs EAC comparison',
        detail: 'Compare the original budget against the EAC for each project to see where the projected outturn is heading over or under budget.',
      },
      {
        label: 'Forecast health indicators',
        detail: 'Each project is colour-coded based on how the EAC compares to budget, making it easy to spot projects that are forecast to overspend.',
      },
      {
        label: 'Portfolio forecast summary',
        detail: 'KPI cards show total budget, total EAC, overall forecast variance, and the number of projects forecast to overspend at a glance.',
      },
      {
        label: 'Search and filter',
        detail: 'Search by project name or client, and filter by forecast health to focus on specific projects.',
      },
      {
        label: 'Export to Excel',
        detail: 'Export the forecast report to Excel for use in financial planning or project reviews.',
      },
    ],

    sectionsAndFields: [
      {
        name: 'KPI summary cards',
        description: 'Displays portfolio-level totals: total budget, total EAC, overall forecast variance, and count of projects forecast to overspend.',
      },
      {
        name: 'Search bar',
        description: 'Filter the project list in real time by project name or client name.',
      },
      {
        name: 'Forecast health filter',
        description: 'Filter projects by their forecast health status — On Track, At Risk, or Forecast to Overspend.',
      },
      {
        name: 'Forecast table',
        description: 'Lists all projects with columns for project name, client, project manager, original budget, actual spend to date, EAC, forecast variance, and forecast health status.',
      },
      {
        name: 'EAC',
        description: 'The Estimate at Completion — the projected total cost of the project based on actual spend and remaining forecast.',
      },
      {
        name: 'Forecast variance',
        description: 'The difference between the original budget and the EAC, showing how much over or under budget the project is projected to finish.',
      },
      {
        name: 'Forecast health indicator',
        description: 'A colour-coded indicator showing whether the project is On Track, At Risk, or Forecast to Overspend based on configured thresholds.',
      },
      {
        name: 'Export button',
        description: 'Downloads the forecast report as an Excel file.',
      },
    ],

    useCases: [
      'Identifying projects that are forecast to overspend before they actually do.',
      'Reviewing portfolio-level forecast variance ahead of a finance review.',
      'Comparing budget to EAC across all projects to prioritise management attention.',
      'Exporting forecast data for use in financial planning or board reporting.',
    ],
  },

  userGuide: {
    gettingStarted: 'The Project Forecast Report loads with all projects that have forecast data. Use the KPI cards for a portfolio summary, then use the filters to focus on projects that are forecast to overspend.',
    steps: [
      {
        title: 'How do I find projects forecast to overspend?',
        items: [
          { text: 'Use the forecast health filter and select "Forecast to Overspend".' },
          { text: 'Look for projects with a red forecast health indicator in the table.' },
          { text: 'Review the forecast variance column to understand the magnitude of the projected overspend.' },
        ],
      },
      {
        title: 'How do I export the report?',
        items: [
          { text: 'Apply any filters you need.' },
          { text: 'Click the Export button in the page header.' },
          { text: 'The report downloads as an Excel file.' },
        ],
      },
    ],
    proTips: [
      'Use this report early in a project\'s lifecycle — catching a forecast overspend early gives you time to take corrective action.',
      'Combine this report with the Budget Variance Report to distinguish between projects that have already overspent and those that are only projected to overspend.',
    ],
  },

  faq: [
    {
      question: 'How is the EAC calculated?',
      answer: 'The Estimate at Completion is calculated based on actual spend to date plus the remaining cost forecast. The calculation method can be configured in Settings by an Administrator.',
    },
    {
      question: 'What is the difference between budget variance and forecast variance?',
      answer: 'Budget variance compares actual spend to budget — what has already been spent. Forecast variance compares the projected total cost (EAC) to budget — what the project is expected to cost in total by completion.',
    },
    {
      question: 'Why does a project show no forecast data?',
      answer: 'A project will only show forecast data if a budget and cost forecast have been entered. If data is missing, check the project\'s budget tab in Project Details.',
    },
    {
      question: 'Can I update forecast figures from this report?',
      answer: 'No. The Forecast Report is read-only. To update forecast data, navigate to the relevant project\'s budget tab in Project Details.',
    },
  ],

  relatedPages: [
    { label: 'Projects', path: '/projects' },
    { label: 'Budget Variance Report', path: '/reports/budget-variance' },
    { label: 'Project P&L Report', path: '/reports/project-pl' },
  ],
};
